import { TOOL_NAMES } from 'js/src/agent/toolNames';
import type { OpaqueVideoData } from '@lumen5/luminary';
import type Luminary from '@lumen5/luminary';

import { MediaRegistry } from '../registries/MediaRegistry';
import { generateVideoDescriptionFromVideoData } from '../video-state';
import { NO_VIDEO_STATE_MESSAGE } from '../video-state/constants';

import { extractReferenceInsights } from './reference-video-insights';
import { describeDesignChanges } from './reference-video-design-changes';
import { describeReferenceLint } from './reference-video-lint';

import type { GetRootState } from 'js/src/libs/RootState';

const REFERENCE_VIDEO_HEADER =
    '## Reference Video State\n' +
    '_This is the reference video — the video to imitate. It is NOT the video you are editing._\n' +
    // Explained here rather than in the always-on asset legend: the marker is only ever emitted
    // for a reference video, so documenting it globally would describe a suffix most agents never
    // see. A value with no marker is simply the scene design's own default.
    "_A property marked `(changed from design default X)` is one this video's author set " +
    'deliberately, where `X` is what its scene design would otherwise have given. Those are the ' +
    'values to reproduce; unmarked ones your own design already supplies._\n\n';

const REFERENCE_MEDIA_HEADER =
    '## Reference Media\n' +
    `_The reference video's media, indexed like search results. Reuse an id with ${TOOL_NAMES.setMedia} where ` +
    'the very same asset belongs in the new video (a logo, a brand clip, an outro image); for ' +
    'everything else, search for media in the same style as these._\n';

/**
 * The reference's media as the agent sees search results — same registry representations, so the
 * ids are directly usable. Empty when the reference carries no media.
 */
function formatReferenceMedia(mediaIds: string[]): string {
    const mediaRegistry = MediaRegistry.getInstance();
    const representations = mediaIds
        .map((id) => mediaRegistry.getRepresentation(id))
        .filter((representation) => representation !== null);
    if (representations.length === 0) {
        return '';
    }
    return `\n\n${REFERENCE_MEDIA_HEADER}${JSON.stringify(representations)}`;
}

/**
 * A video described as a reference to imitate: its style summary, its scene-by-scene annotation
 * with the values its author changed from the design defaults marked, its media and its own lint
 * findings. Null when the video has no scenes (the serializer's "no video yet" message describes
 * the agent's own video, which is exactly what a reference must not be confused with).
 */
export async function describeReferenceVideo(
    videoData: OpaqueVideoData,
    getState: GetRootState,
    templateBundle?: Parameters<Luminary['setTemplate']>[0] | null,
    options: { includeDesignChanges?: boolean } = {},
): Promise<string | null> {
    let referenceMediaIds: string[] = [];
    let insights = '';
    let referenceLint = '';
    let designChanges = '';
    const annotation = await generateVideoDescriptionFromVideoData(videoData, {
        header: REFERENCE_VIDEO_HEADER,
        getState,
        // This video is being imitated, not edited, so which of its values its author chose
        // deliberately is exactly what the agent needs to reproduce.
        markDesignDefaults: true,
        ...(templateBundle ? { templateBundle } : {}),
        // Index the reference's media before it is serialized, so every media asset in the
        // annotation carries a real title and an id set_media accepts. Built from the assets'
        // own source data: a blueprint's example can sit in a project the user isn't in, where
        // fetching its media by uuid would be denied.
        onLoaded: async (luminary) => {
            referenceMediaIds = MediaRegistry.getInstance().addUnseenItemsFromSourceData(luminary);
            // Same loaded engine the serializer uses, so the summary is read off exactly the
            // state the annotation describes.
            insights = extractReferenceInsights(luminary);
            referenceLint = await describeReferenceLint(luminary, getState);
            if (options.includeDesignChanges) {
                designChanges = describeDesignChanges(luminary, videoData);
            }
        },
    });
    if (annotation === NO_VIDEO_STATE_MESSAGE) {
        return null;
    }
    // The summary leads: the rules it states are the ones most often missed when they have to
    // be inferred from the scene dump that follows.
    const lintSection = referenceLint ? `\n\n${referenceLint}` : '';
    const designChangesSection = designChanges ? `\n\n${designChanges}` : '';
    return `${insights}\n${annotation}${formatReferenceMedia(referenceMediaIds)}${lintSection}${designChangesSection}`;
}
